import { assert } from 'assert-ts';
import { useId, useState } from 'react';
import { OSIcon } from '#components/atoms/cards/os-card/OSIcon.tsx';
import { Code } from '#components/atoms/code/Code.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { RadioGroup } from '#components/patterns/forms/radio/index.ts';
import { useRadioGroup } from '#components/patterns/forms/radio/useRadioGroup.ts';
import type { OSGroup } from '#dn-types/downloads.ts';
import type { Host, HostStatus } from '#dn-types/host.ts';
import { getIpV6 } from '#util/hostIps.ts';
import type { GetStartedHostStatus } from './GetStarted.queries.ts';
import { defaultPingSource, osGroupFromMetadata, pingCommand } from './ping-command.ts';
import styles from './PingTestSection.module.css';

/**
 * The done-step "Try it out" ping: a "Run from" host pill per host (OS icon + name) driving a
 * single command aimed at the other host. Purely presentational — GetStarted owns the live host
 * results and passes them down. Each host's OS comes solely from its detected metadata: the api
 * writes it in the same request that consumes the enrollment code, so detection has landed before
 * this done-step section can mount, and until the parent's results reflect it the section renders
 * nothing rather than a guessed command. Selecting a phone shows prose instead of an unrunnable
 * command, with the command inline for the rare phone that does have a terminal.
 */
export function PingTestSection({ hosts }: { hosts: readonly [GetStartedHostStatus, GetStartedHostStatus] }) {
  // Radio ids/names must be instance-unique: themed stories mount two copies of the flow on one
  // page, and duplicated ids cross-wire the label associations between copies.
  const sectionId = useId();
  const [first, second] = hosts;
  // Follow the computed default (lead with a host that can run a command) until the user picks
  // explicitly.
  const [pickedIndex, setPickedIndex] = useState<0 | 1 | null>(null);

  const firstOS = detectedOSGroup(first);
  const secondOS = detectedOSGroup(second);
  if (firstOS === null || secondOS === null) return null;

  const resolved = [resolvePingHost(first.host, firstOS), resolvePingHost(second.host, secondOS)] as const;
  const fromIndex = pickedIndex ?? defaultPingSource(resolved[0].os, resolved[1].os);
  const from = resolved[fromIndex];
  const target = resolved[fromIndex === 0 ? 1 : 0];
  // null only when the selected host is a phone (no terminal) — the prose below covers it.
  const command = pingCommand(from.os, target.ipv6);

  return (
    <div className={styles.PingTestSection}>
      <div className={styles.PingTestSection_control}>
        <div className={styles.PingTestSection_controlLabel} id={`${sectionId}-from-label`}>
          Run from
        </div>
        <RadioGroup
          name={`${sectionId}-ping-from`}
          value={String(fromIndex)}
          onChange={(e) => setPickedIndex(e.currentTarget.value === '1' ? 1 : 0)}
        >
          {/* RadioGroup is context-only (renders no element), so the group role + name live here */}
          <div className={styles.PingTestSection_pills} role="radiogroup" aria-labelledby={`${sectionId}-from-label`}>
            {resolved.map((host, i) => (
              <PillRadio key={host.id} id={`${sectionId}-ping-from-${i}`} value={String(i)}>
                <OSIcon os={host.os} />
                {host.name}
                {/* The OS is otherwise only in the (unnamed) icon — name it for non-visual users,
                    who need it to tell which option is the phone before selecting. */}
                <span className="visually-hidden">({host.os})</span>
              </PillRadio>
            ))}
          </div>
        </RadioGroup>
      </div>
      {/* aria-live: the command respells in place when the pills switch */}
      <div aria-live="polite">
        {command !== null ?
          <Snippet className="m-0">{command}</Snippet>
        : <p className={styles.PingTestSection_prose}>
            {from.name} is a phone, and phones ship without a ping tool — but your hosts can already reach each other
            over this network. If it has a terminal (like Termux), try <Code>ping {target.ipv6}</Code>.
          </p>
        }
      </div>
    </div>
  );
}

// The api writes the detected OS and lastSeenAt together on every client request — including the
// enroll request itself — so an online host with no reported OS means a User-Agent with no
// parseable OS comment (pre-2023 dnclient, or a UA-mangling proxy — never observed from a real
// client in prod metrics): an invariant break to fail loudly on, not a state to design UI for.
// Null is only the moments before the cache reflects the client's first check-in.
function detectedOSGroup({ host, status }: { host: Host; status: HostStatus }): OSGroup | null {
  const os = osGroupFromMetadata(host.metadata.os);
  if (os === null) {
    assert(status !== 'online', 'online host must have a detected OS');
  }
  return os;
}

function resolvePingHost(live: Host, os: OSGroup) {
  return {
    id: live.id,
    name: live.name,
    os,
    // A missing IPv6 on a v2-network host is an API invariant break, not a state to design UI for.
    ipv6: assert(getIpV6(live), 'v2 host must have an IPv6 address'),
  };
}

// One selectable pill, following the install picker's OS-tab pattern (visually-hidden native radio
// + styled label) so the group keeps real radio semantics.
function PillRadio({ id, value, children }: { id: string; value: string; children: React.ReactNode }) {
  const { name, selectedValue, onChange } = useRadioGroup();
  const selected = String(selectedValue) === value;

  return (
    <>
      <input
        className={`visually-hidden ${styles.PingTestSection_pillRadio}`}
        id={id}
        name={name}
        type="radio"
        value={value}
        checked={selected}
        onChange={onChange}
      />
      <label
        htmlFor={id}
        className={
          selected ?
            `${styles.PingTestSection_pill} ${styles.PingTestSection_pill___selected}`
          : styles.PingTestSection_pill
        }
      >
        {children}
      </label>
    </>
  );
}
